// @flow
import AdvertisementsTable from './AdvertisementsTable'
import { connect } from 'react-redux'
import * as actions from '../redux/ducks/advertisementTable/reducers'
import _ from 'lodash'
import { calculateStatistics } from '../services/statistics/utils'
import type { TableItem, Advertisement, AdvertisementStatistics } from './AdvertisementsTable'

type OwnProps = {|
  initialTableItems: Array<TableItem>,
  datepicker: string,
  newAdvertisementPath: string,
  statisticsPath: string,
  sortField: string,
  sortDirection: string,
  dateSeparator: string,
  filterFormOrigin: string,
  statistics: Array<AdvertisementStatistics>,
  isImportedAudience: boolean
|}

function mapStateToProps(state, ownProps: OwnProps) {
  const {
    tableItems,
    sortField,
    sortDirection,
    providersList,
    statisticsType,
    statistics,
    archivedAdvertisementIds,
    detachedAdvertisementIds,
    checkedAdvertisementIds,
    isFilterFormChanged,
    isLoading,
    selectedAdvertisementTemplateIds,
    advertisementTemplateOptions
  } = state.advertisementTable

  const validTableItems = _.map(tableItems, (item) => {
    return {
      ...item,
      advertisements: _.reject(
        _.map(item.advertisements, (ad) => {
          return {
            ...ad,
            advertisement_template_id: _.includes(detachedAdvertisementIds, ad.id)
              ? null
              : ad.advertisement_template_id
          }
        }),
        (ad) =>
          _.includes(archivedAdvertisementIds, ad.id) ||
          (!_.isEmpty(selectedAdvertisementTemplateIds) &&
            !_.includes(selectedAdvertisementTemplateIds, ad.advertisement_template_id))
      )
    }
  })

  const adStatistics = calculateAdvertisementStatistics(
    statisticsType,
    statistics,
    _.flatten(_.map(validTableItems, 'advertisements'))
  )

  const tableItemsWithSortedAdvertisements = _.filter(
    _.map(validTableItems, (item) => {
      return {
        ...item,
        advertisements: _.filter(
          sortAdvertisements(
            item.advertisements,
            adStatistics,
            sortField,
            sortDirection,
            statisticsType,
            providersList
          ),
          (ad) => !_.isEmpty(adStatistics[ad.id])
        )
      }
    }),
    (item) => !_.isEmpty(item.advertisements)
  )

  return {
    tableItems,
    archivedAdvertisementIds,
    detachedAdvertisementIds,
    checkedAdvertisementIds,
    isFilterFormChanged,
    isLoading,
    adStatistics,
    visibleTableItems: tableItemsWithSortedAdvertisements,
    initialTableItems: ownProps.initialTableItems,
    providersList,
    statisticsType,
    selectedAdvertisementTemplateIds,
    advertisementTemplateOptions
  }
}

// group statistics by advertisement ids
function calculateAdvertisementStatistics(
  statisticsType,
  statistics,
  notArchivedAdvertisements
): { [number]: Array<AdvertisementStatistics> } {
  return _.fromPairs(
    _.map(notArchivedAdvertisements, (advertisement: Advertisement) => {
      const currentStatistics = _.filter(statistics, { advertisementId: advertisement.id })
      const calculatedStatistics = calculateStatistics(
        statisticsType,
        currentStatistics,
        advertisement.providers_list
      )

      return [advertisement.id, calculatedStatistics]
    })
  )
}

function sortAdvertisements(
  advertisements: Array<Advertisement>,
  adStatistics: { [number]: Array<AdvertisementStatistics> },
  sortField,
  sortDirection,
  statisticsType,
  providersList
): Array<Advertisement> {
  // sort by advertisement
  if (_.includes(['advertisement'], sortField)) {
    const direction = sortDirection === 'asc' ? 1 : -1

    return advertisements.sort((a, b) => {
      let aSort = a.title
      let bSort = b.title

      if (aSort < bSort) {
        return -direction
      } else if (aSort > bSort) {
        return direction
      } else {
        return 0
      }
    })
  }

  // when statisticsType is overall or equals a provider
  if (statisticsType === 'overall' || _.includes(providersList, statisticsType)) {
    const groups = _.groupBy(advertisements, (advertisement) => {
      const statistics = adStatistics[advertisement.id]

      return statistics.length === 0 ? 'notSortable' : 'sortable'
    })
    const direction = sortDirection === 'asc' ? 1 : -1

    const sortable = groups.sortable || []
    const notSortable = groups.notSortable || []
    const sorted = _.sortBy(
      sortable,
      (advertisement) => direction * adStatistics[advertisement.id][0][sortField]
    )

    return [...sorted, ...notSortable]
  }

  // otherwise not to sort
  return advertisements
}

function mapDispatchToProps(dispatch: Function) {
  return {
    onChange: (state) => {
      dispatch(actions.updateState(state))
    }
  }
}

const AdvertisementsTableContainer = connect<Object, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps
)(AdvertisementsTable)

export default AdvertisementsTableContainer
